import React, { type FC, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLatest } from 'react-use';
import { type PayPalFundingSource } from '../../../../types/PaymentsButtonViewConfig';
import { type PayPalButtonColor, type PayPalButtonParams } from '../../../../types/paypal';
import { selectPayPalBtnColor, selectTopProps } from '../../../../selectors';
import { logGeneralError } from '../../../../utils/sentry';
import s from '../styles.css';

export interface Props {
  fundingSource: PayPalFundingSource;
  widgetRootWrapperDh: string;
  createOrder: PayPalButtonParams['createOrder'];
  onClick: PayPalButtonParams['onClick'];
  onApprove: PayPalButtonParams['onApprove'];
  onCancel: PayPalButtonParams['onCancel'];
  onShippingChange?: PayPalButtonParams['onShippingChange'];
}

const onError = (err: unknown) => {
  logGeneralError({
    message: 'PayPal onError cb',
    payload: err,
    fingerprint: ['onError cb'],
  });
};

export const PayPalButton: FC<Props> = (props) => {
  const topProps = useSelector(selectTopProps);
  const color = useSelector(selectPayPalBtnColor);
  const buttonRef = useRef(null);
  const buttonsContainerRef = useRef<HTMLDivElement>(null);
  const hasRenderingError = useRef<boolean>(false);
  const propsRef = useLatest(props);
  const dataHook = `ecw-button-wrapper-${props.fundingSource}`;

  const closeButtonsComponent = () => {
    if (buttonRef.current !== null) {
      buttonRef.current.close();
    }
  };

  // useEffect hook for rendering the buttons
  // originaly is taken from https://github.com/paypal/react-paypal-js/blob/main/src/components/PayPalButtons.tsx#L55
  useEffect(() => {
    try {
      const BUTTON_COLOR: Partial<Record<PayPalFundingSource, PayPalButtonColor>> = {
        [window.paypal.FUNDING.VENMO]: 'blue',
        [window.paypal.FUNDING.CREDIT]: 'darkblue',
      };

      buttonRef.current = window.paypal.Buttons({
        style: {
          color: BUTTON_COLOR[props.fundingSource] || color,
          shape: topProps.buttonStyle.shape,
          tagline: false,
          label: 'checkout',
          height: topProps.buttonStyle.height,
        },
        fundingSource: props.fundingSource,
        onError,

        // We need to provide the callbacks that might change dynamically WITHOUT the need to re-render the Buttons.
        // This is achieved by useLatest(props) and wrapping each callback like we do here.
        // This way e.g. props.onClick can change and the new value will be used by the Buttons without re-rendering them.
        onClick: (...args) => propsRef.current.onClick(...args),
        createOrder: (...args) => propsRef.current.createOrder(...args),
        onApprove: (...args) => propsRef.current.onApprove(...args),
        onCancel: (...args) => propsRef.current.onCancel(...args),
        ...(props.onShippingChange && {
          onShippingChange: (...args) => propsRef.current.onShippingChange(...args),
        }),
      });

      // only render the button when eligible
      if (buttonRef.current.isEligible() === false) {
        return closeButtonsComponent;
      }

      if (buttonsContainerRef.current === null) {
        return closeButtonsComponent;
      }

      buttonRef.current.render(buttonsContainerRef.current).catch((err: unknown) => {
        // component failed to render, possibly because it was closed or destroyed.
        if (buttonsContainerRef.current === null || buttonsContainerRef.current.children.length === 0) {
          // paypal button is no longer in the DOM, we can safely ignore the error
          return;
        }
        console.error(new Error(`Failed to render <PayPalButtons /> component. ${err}`));
      });
    } catch (err: unknown) {
      hasRenderingError.current = true;

      logGeneralError({
        message: `PayPal ${props.fundingSource} button`,
        fingerprint: ['onError', props.fundingSource],
        payload: err,
      });
    }

    return closeButtonsComponent;
  }, [topProps.theme, topProps.buttonStyle.height, topProps.buttonStyle.shape]);

  if (hasRenderingError.current) {
    return null;
  }

  return (
    <div
      ref={buttonsContainerRef}
      data-hook={dataHook}
      className={s.wrapper}
      style={{ height: topProps.buttonStyle.height }}
    />
  );
};
